import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaFileExcel, FaTimes } from 'react-icons/fa';
import Loading from '~/Components/Loading';
import { salvarEmExcel } from '~/Utils/Helpers';
import {
  buscarResumoFluxoCaixaFcDw,
} from '~/Services/Service';
import GraficoFluxoPorAno from './GraficoFluxoPorAno';
import GraficoSituacaoContrato from './GraficoSituacaoContrato';
import CelulaComTooltip from './CelulaComTooltip';
import TabelaFluxoCaixa from './TabelaFluxoCaixa';
import {
  FILTROS_VISUAIS_INICIAIS,
  alternarFiltroVisual,
  calcularDadosInterativos,
  criarFiltroEmpreendimento,
  empreendimentoEstaSelecionado,
  obterFiltrosVisuaisAtivos,
  tipoParcelaEstaSelecionado,
} from '../interacoes';
import {
  criarChaveFiltros,
  formatarMoeda,
  formatarPercentual,
  getMensagemErro,
  normalizarLista,
} from '../utils';

const TEMPO_CACHE_RESUMO_MS = 30 * 60 * 1000;
const LIMITE_CACHE_RESUMO_LOCAL = 10;
const resumoFluxoCaixaCache = {};
const resumoFluxoCaixaRequests = {};

const obterResumoCache = chave => {
  const item = resumoFluxoCaixaCache[chave];
  if (!item) return null;

  if (item.expiraEm <= Date.now()) {
    delete resumoFluxoCaixaCache[chave];
    return null;
  }

  return item.response;
};

const salvarResumoCache = (chave, response) => {
  resumoFluxoCaixaCache[chave] = {
    response,
    expiraEm: Date.now() + TEMPO_CACHE_RESUMO_MS,
  };

  const chaves = Object.keys(resumoFluxoCaixaCache);
  if (chaves.length > LIMITE_CACHE_RESUMO_LOCAL) {
    delete resumoFluxoCaixaCache[chaves[0]];
  }
};

const buscarResumoComCache = filtros => {
  const chaveCache = criarChaveFiltros(filtros);
  const resumoCache = obterResumoCache(chaveCache);
  if (resumoCache) {
    return Promise.resolve(resumoCache);
  }

  if (resumoFluxoCaixaRequests[chaveCache]) {
    return resumoFluxoCaixaRequests[chaveCache];
  }

  resumoFluxoCaixaRequests[chaveCache] = buscarResumoFluxoCaixaFcDw(filtros)
    .then(response => {
      salvarResumoCache(chaveCache, response);
      delete resumoFluxoCaixaRequests[chaveCache];
      return response;
    })
    .catch(err => {
      delete resumoFluxoCaixaRequests[chaveCache];
      throw err;
    });

  return resumoFluxoCaixaRequests[chaveCache];
};

const obterValorComAliases = (item, campos) => {
  if (!item) return null;
  const campo = campos.find(chave => item[chave] != null);
  return campo ? item[campo] : null;
};

const normalizarLinhaValoresEmpreendimento = item => ({
  ...(item || {}),
  acima_de_30: obterValorComAliases(item, ['acima_de_30', 'acima_de30']),
  acima_de_90: obterValorComAliases(item, ['acima_de_90', 'acima_de90']),
  acima_de_150: obterValorComAliases(item, [
    'acima_de_150',
    'acima_de150',
  ]),
});

const normalizarValoresEmpreendimento = response => {
  const itens = normalizarLista(
    response && response.itens ? response.itens : []
  ).map(normalizarLinhaValoresEmpreendimento);
  const linhas = itens.map(item => ({
    ...item,
    id:
      item.codigo_empreendimento != null
        ? `emp-${item.codigo_empreendimento}`
        : `emp-${item.empreendimento}`,
  }));

  if (response && response.total) {
    linhas.push({
      ...normalizarLinhaValoresEmpreendimento(response.total),
      empreendimento: 'Total',
      id: 'total-valores-empreendimento',
      is_total: true,
    });
  }

  return linhas;
};

const normalizarTipoParcela = response => {
  const itens = normalizarLista(
    response && response.itens ? response.itens : []
  );
  const linhas = itens.map(item => ({
    ...item,
    id: `tipo-${item.tipo_parcela}`,
  }));

  if (response && response.total) {
    linhas.push({
      ...response.total,
      tipo_parcela: 'Total',
      id: 'total-tipo-parcela',
      is_total: true,
    });
  }

  return linhas;
};

const renderTexto = (value, id) => (
  <CelulaComTooltip id={id} texto={value || '-'}>
    {value || '-'}
  </CelulaComTooltip>
);

const renderNumero = value => (
  <span className="fluxo-caixa-dw-number">{formatarMoeda(value)}</span>
);

const renderValorTotal = (value, item) =>
  item.is_total ? (
    <strong>{formatarMoeda(value)}</strong>
  ) : (
    renderNumero(value)
  );

const renderPercentualCobranca = (value, item) => (
  <span className="fluxo-caixa-dw-percentual-cobranca">
    {item.fora_da_meta === true && <span className="fluxo-caixa-dw-meta-dot" />}
    {item.is_total ? (
      <strong>{formatarPercentual(value)}</strong>
    ) : (
      <span className="fluxo-caixa-dw-number">
        {formatarPercentual(value)}
      </span>
    )}
  </span>
);

const montarPayloadExportacaoTabela = (filtros, linhas) => {
  const normalizarLinha = item => ({
    ...item,
    acima_de30:
      item && item.acima_de30 != null ? item.acima_de30 : item.acima_de_30,
    acima_de90:
      item && item.acima_de90 != null ? item.acima_de90 : item.acima_de_90,
    acima_de150:
      item && item.acima_de150 != null ? item.acima_de150 : item.acima_de_150,
  });
  const itens = normalizarLista(linhas)
    .filter(item => !item.is_total)
    .map(normalizarLinha);
  const totalOriginal =
    normalizarLista(linhas).find(item => item.is_total) || null;

  return {
    ...filtros,
    itens,
    total: totalOriginal ? normalizarLinha(totalOriginal) : null,
  };
};

const AbaFluxoCaixa = ({ filtros, onDadosCarregados }) => {
  const [loading, setLoading] = useState(false);
  const [valoresEmpreendimento, setValoresEmpreendimento] = useState([]);
  const [tiposParcela, setTiposParcela] = useState([]);
  const [fluxoPorAno, setFluxoPorAno] = useState([]);
  const [situacaoContrato, setSituacaoContrato] = useState([]);
  const [interacoes, setInteracoes] = useState({});
  const [filtrosVisuais, setFiltrosVisuais] = useState(
    FILTROS_VISUAIS_INICIAIS
  );

  useEffect(() => {
    let ativo = true;

    const carregarDados = async () => {
      setLoading(true);
      setFiltrosVisuais(FILTROS_VISUAIS_INICIAIS);
      const aplicarResumo = resumoResponse => {
        const valoresResponse =
          resumoResponse && resumoResponse.valores_empreendimento
            ? resumoResponse.valores_empreendimento
            : null;
        const tipoParcelaResponse =
          resumoResponse && resumoResponse.tipo_parcela
            ? resumoResponse.tipo_parcela
            : null;
        const fluxoResponse =
          resumoResponse && resumoResponse.fluxo_por_ano
            ? resumoResponse.fluxo_por_ano
            : null;
        const situacaoResponse =
          resumoResponse && resumoResponse.situacao_contrato
            ? resumoResponse.situacao_contrato
            : null;

        setValoresEmpreendimento(
          normalizarValoresEmpreendimento(valoresResponse)
        );
        setTiposParcela(normalizarTipoParcela(tipoParcelaResponse));
        setFluxoPorAno(
          normalizarLista(
            fluxoResponse && fluxoResponse.itens ? fluxoResponse.itens : []
          )
        );
        setSituacaoContrato(
          normalizarLista(
            situacaoResponse && situacaoResponse.itens
              ? situacaoResponse.itens
              : []
          )
        );
        setInteracoes(
          resumoResponse && resumoResponse.interacoes
            ? resumoResponse.interacoes
            : {}
        );
      };

      try {
        const resumoResponse = await buscarResumoComCache(filtros);

        if (!ativo) return;

        aplicarResumo(resumoResponse);
        if (onDadosCarregados) {
          onDadosCarregados();
        }
      } catch (err) {
        if (ativo) {
          toast.error(getMensagemErro(err, 'Erro ao carregar Fluxo de Caixa.'));
        }
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    };

    carregarDados();

    return () => {
      ativo = false;
    };
  }, [filtros, onDadosCarregados]);

  const dadosInterativos = useMemo(
    () =>
      calcularDadosInterativos(
        {
          valoresEmpreendimento,
          tiposParcela,
          fluxoPorAno,
          situacaoContrato,
          interacoes,
        },
        filtrosVisuais
      ),
    [
      valoresEmpreendimento,
      tiposParcela,
      fluxoPorAno,
      situacaoContrato,
      interacoes,
      filtrosVisuais,
    ]
  );

  const filtrosAtivos = useMemo(
    () => obterFiltrosVisuaisAtivos(filtrosVisuais),
    [filtrosVisuais]
  );

  const selecionarEmpreendimento = item => {
    if (!item || item.is_total) return;
    const filtro = criarFiltroEmpreendimento(item);
    setFiltrosVisuais(anteriores =>
      alternarFiltroVisual(anteriores, 'empreendimento', filtro)
    );
  };

  const selecionarTipoParcela = item => {
    if (!item || item.is_total) return;
    setFiltrosVisuais(anteriores =>
      alternarFiltroVisual(anteriores, 'tipo_parcela', item.tipo_parcela)
    );
  };

  const selecionarSituacao = situacao => {
    if (!situacao) return;
    setFiltrosVisuais(anteriores =>
      alternarFiltroVisual(anteriores, 'situacao', situacao)
    );
  };

  const colunasValores = useMemo(
    () => [
      {
        title: 'Empreendimento',
        key: 'empreendimento',
        ordenacao: true,
        width: '218px',
        render: (value, item) =>
          item.is_total ? <strong>Total</strong> : renderTexto(value, item.id),
      },
      {
        title: 'Valor',
        key: 'valor',
        align: 'right',
        ordenacao: true,
        width: '132px',
        render: renderValorTotal,
        excel: item => formatarMoeda(item.valor),
      },
      {
        title: 'Comissão',
        key: 'comissao',
        align: 'right',
        ordenacao: true,
        width: '118px',
        render: renderValorTotal,
        excel: item => formatarMoeda(item.comissao),
      },
      {
        title: 'Cobrança',
        key: 'cobranca',
        align: 'right',
        ordenacao: true,
        width: '128px',
        render: renderValorTotal,
        excel: item => formatarMoeda(item.cobranca),
      },
      {
        title: '% Cob.',
        key: 'percentual_cobranca',
        align: 'right',
        ordenacao: true,
        width: '116px',
        render: renderPercentualCobranca,
        excel: item => formatarPercentual(item.percentual_cobranca),
      },
      {
        title: 'Acima de 30',
        key: 'acima_de_30',
        align: 'right',
        ordenacao: true,
        width: '128px',
        render: renderValorTotal,
        excel: item => formatarMoeda(item.acima_de_30),
      },
      {
        title: 'Acima de 90',
        key: 'acima_de_90',
        align: 'right',
        ordenacao: true,
        width: '128px',
        render: renderValorTotal,
        excel: item => formatarMoeda(item.acima_de_90),
      },
      {
        title: 'Acima de 150',
        key: 'acima_de_150',
        align: 'right',
        ordenacao: true,
        width: '128px',
        render: renderValorTotal,
        excel: item => formatarMoeda(item.acima_de_150),
      },
    ],
    []
  );

  const colunasTipoParcela = useMemo(
    () => [
      {
        title: 'Tipo de Parcela',
        key: 'tipo_parcela',
        ordenacao: true,
        width: '185px',
        render: (value, item) =>
          item.is_total ? <strong>Total</strong> : renderTexto(value, item.id),
      },
      {
        title: 'Valor a Receber',
        key: 'valor_a_receber',
        align: 'right',
        ordenacao: true,
        width: '150px',
        render: renderValorTotal,
        excel: item => formatarMoeda(item.valor_a_receber),
      },
      {
        title: 'Comissão',
        key: 'comissao',
        align: 'right',
        ordenacao: true,
        width: '118px',
        render: renderValorTotal,
        excel: item => formatarMoeda(item.comissao),
      },
    ],
    []
  );

  const exportarValores = async () => {
    try {
      await salvarEmExcel(
        montarPayloadExportacaoTabela(
          filtros,
          dadosInterativos.valoresEmpreendimento
        ),
        '/exportador/excel/fluxo-caixa-dw/valores-empreendimento'
      );
    } catch (err) {
      toast.error(
        getMensagemErro(err, 'Erro ao exportar Valores por Empreendimento.')
      );
    }
  };

  const exportarTipoParcela = async () => {
    try {
      await salvarEmExcel(
        montarPayloadExportacaoTabela(filtros, dadosInterativos.tiposParcela),
        '/exportador/excel/fluxo-caixa-dw/tipo-parcela'
      );
    } catch (err) {
      toast.error(getMensagemErro(err, 'Erro ao exportar Tipo de Parcela.'));
    }
  };

  if (loading) {
    return <Loading label="Carregando Fluxo de Caixa..." />;
  }

  return (
    <>
      {filtrosAtivos.length > 0 && (
        <div className="fluxo-caixa-dw-cross-filter-bar">
          <div className="fluxo-caixa-dw-cross-filter-tags">
            {filtrosAtivos.map(filtro => (
              <span
                className="fluxo-caixa-dw-cross-filter-tag"
                key={filtro.key}
              >
                {filtro.label}
              </span>
            ))}
          </div>
          <Button
            onClick={() => setFiltrosVisuais(FILTROS_VISUAIS_INICIAIS)}
            size="sm"
            type="button"
            variant="outline-secondary"
          >
            <FaTimes className="mr-1" />
            Limpar seleção
          </Button>
        </div>
      )}

      <div className="fluxo-caixa-dw-dashboard-grid">
        <div className="fluxo-caixa-dw-panel fluxo-caixa-dw-panel--fixed">
          <div className="fluxo-caixa-dw-panel-header">
            <h3 className="fluxo-caixa-dw-panel-title">
              <span className="fluxo-caixa-dw-title-marker" />
              Valores por Empreendimento
            </h3>
            <Button
              disabled={dadosInterativos.valoresEmpreendimento.length === 0}
              onClick={exportarValores}
              size="sm"
              variant="outline-success"
            >
              <FaFileExcel className="mr-1" />
              Excel
            </Button>
          </div>
          <div className="fluxo-caixa-dw-panel-body fluxo-caixa-dw-panel-body--table">
            <TabelaFluxoCaixa
              cellLineHeight="32px"
              cellPadding="0 8px"
              cellWhiteSpace="nowrap"
              cliqueLinha={selecionarEmpreendimento}
              colunas={colunasValores}
              dados={dadosInterativos.valoresEmpreendimento}
              fontSize="12px"
              headerBgColor="#0b4f9c"
              headerLineHeight="34px"
              headerPadding="0 8px"
              linhaSelecionada={item =>
                empreendimentoEstaSelecionado(filtrosVisuais, item)
              }
              minWidth="1123px"
              tableLayout="fixed"
            />
          </div>
        </div>

        <div className="fluxo-caixa-dw-panel fluxo-caixa-dw-panel--fixed">
          <div className="fluxo-caixa-dw-panel-header">
            <h3 className="fluxo-caixa-dw-panel-title">
              <span className="fluxo-caixa-dw-title-marker" />
              Tipo de Parcela
            </h3>
            <Button
              disabled={dadosInterativos.tiposParcela.length === 0}
              onClick={exportarTipoParcela}
              size="sm"
              variant="outline-success"
            >
              <FaFileExcel className="mr-1" />
              Excel
            </Button>
          </div>
          <div className="fluxo-caixa-dw-panel-body fluxo-caixa-dw-panel-body--table">
            <TabelaFluxoCaixa
              cellLineHeight="32px"
              cellPadding="0 8px"
              cellWhiteSpace="nowrap"
              cliqueLinha={selecionarTipoParcela}
              colunas={colunasTipoParcela}
              dados={dadosInterativos.tiposParcela}
              fontSize="12px"
              headerBgColor="#0b4f9c"
              headerLineHeight="34px"
              headerPadding="0 8px"
              linhaSelecionada={item =>
                tipoParcelaEstaSelecionado(filtrosVisuais, item)
              }
              minWidth="453px"
              tableLayout="fixed"
            />
          </div>
        </div>

        <div className="fluxo-caixa-dw-panel fluxo-caixa-dw-panel--fixed">
          <div className="fluxo-caixa-dw-panel-header">
            <h3 className="fluxo-caixa-dw-panel-title">
              <span className="fluxo-caixa-dw-title-marker" />
              Fluxo por Ano
            </h3>
          </div>
          <div className="fluxo-caixa-dw-panel-body fluxo-caixa-dw-chart">
            <GraficoFluxoPorAno dados={dadosInterativos.fluxoPorAno} />
          </div>
        </div>

        <div className="fluxo-caixa-dw-panel fluxo-caixa-dw-panel--fixed">
          <div className="fluxo-caixa-dw-panel-header">
            <h3 className="fluxo-caixa-dw-panel-title">
              <span className="fluxo-caixa-dw-title-marker" />
              Situação do Contrato
            </h3>
          </div>
          <div className="fluxo-caixa-dw-panel-body fluxo-caixa-dw-chart">
            <GraficoSituacaoContrato
              dados={dadosInterativos.situacaoContrato}
              onSelecionar={selecionarSituacao}
              situacaoSelecionada={filtrosVisuais.situacao}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AbaFluxoCaixa;
